/**
 * @Description 机会点详情-跟进记录抽屉
 */
import { FC, useEffect, useState } from 'react';
import { isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { postChancePointFollowRecordAdd, postChancePointFollowRecordPage } from '@/common/api/expandStore/chancepoint';
import { Button, Form, Spin } from 'antd';
import V2Container from '@/common/components/Data/V2Container';
import V2Pagination from '@/common/components/Data/V2Pagination';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import CreateFollowRecordModal from './CreateFollowRecordModal';
import FollowRecordContent from './FollowRecordContent';
import styles from './FollowRecordDrawer.module.less';

interface FollowRecordDrawerProps {
  chancePointId: number;
  open: boolean;
  onClose: () => void;
  onRecordAdded: (totalNum: number) => void;
}

const PAGE_SIZE = 20;

/**
 * 机会点详情跟进记录抽屉
 */
const FollowRecordDrawer: FC<FollowRecordDrawerProps> = ({
  chancePointId,
  open,
  onClose,
  onRecordAdded,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [followRecordList, setFollowRecordList] = useState<any[]>([]);
  const [createVisible, setCreateVisible] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [totalNum, setTotalNum] = useState<number>(0);

  const methods = useMethods({
    /** 获取机会点跟进记录分页数据。 */
    getFollowRecords(page = currentPage, size = pageSize, needUpdateRecordCount = false) {
      if (!chancePointId) return;

      setLoading(true);
      postChancePointFollowRecordPage({
        chancePointId,
        page,
        size,
      }).then((data) => {
        const { objectList, totalNum: total, pageNum, pageSize: currentPageSize } = data || {};
        const recordTotal = Number(total) || 0;

        setFollowRecordList(isArray(objectList) ? objectList : []);
        setCurrentPage(pageNum || page);
        setPageSize(currentPageSize || size);
        setTotalNum(recordTotal);
        if (needUpdateRecordCount) {
          // 新增后使用列表接口返回的总数同步父级角标，确保两处数据来源一致。
          onRecordAdded(recordTotal);
        }
      }).finally(() => {
        setLoading(false);
      });
    },

    /** 打开新增跟进表单。 */
    openCreateModal() {
      form.setFieldValue('name', `第${totalNum + 1}次跟进`);
      setCreateVisible(true);
    },

    /** 关闭新增跟进表单并重置字段。 */
    closeCreateModal() {
      setCreateVisible(false);
      form.resetFields();
    },

    /** 提交新增机会点跟进记录。 */
    submitFollowRecord() {
      form.validateFields().then((values: any) => {
        const fileList = isArray(values.fileList) ? values.fileList.map((item) => item.url) : [];

        postChancePointFollowRecordAdd({
          chancePointId,
          name: values.name,
          communicateAt: values.communicateAt,
          content: values.content,
          fileList,
        }).then((res) => {
          if (!res) return;

          methods.closeCreateModal();
          setCurrentPage(1);
          // 新增记录默认位于倒序列表第一页，重新查询后同步列表和父级总数。
          methods.getFollowRecords(1, pageSize, true);
          V2Message.success('新增跟进记录成功');
        });
      });
    },

    /** 切换分页并重新获取对应页数据。 */
    handlePageChange(page: number, size: number) {
      setCurrentPage(page);
      setPageSize(size);
      methods.getFollowRecords(page, size);
    },

    /** 关闭跟进记录抽屉。 */
    closeDrawer() {
      methods.closeCreateModal();
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      methods.getFollowRecords(1, PAGE_SIZE);
    }
  }, [open, chancePointId]);

  return (
    <V2Drawer
      open={open}
      onClose={methods.closeDrawer}
      destroyOnClose
      contentWrapperStyle={{
        width: '450px',
        minWidth: '450px',
        maxWidth: '450px',
      }}
      className={styles.followRecordDrawerCon}
    >
      <Spin spinning={loading}>
        <V2Container
          style={{ height: '100vh' }}
          extraContent={{
            top: (
              <V2Title
                text='跟进记录'
                className='pt-16 pl-24 pr-24 pb-16'
                extra={
                  <Button
                    type='primary'
                    onClick={methods.openCreateModal}
                  >
                    新增跟进
                  </Button>
                }
              />
            ),
            bottom: totalNum > 0 && <V2Pagination
              simple
              current={currentPage}
              total={totalNum}
              pageSize={pageSize}
              showQuickJumper={false}
              showSizeChanger={false}
              onChange={methods.handlePageChange}
            />,
          }}
        >
          <FollowRecordContent records={followRecordList} />
        </V2Container>
      </Spin>
      <CreateFollowRecordModal
        form={form}
        open={createVisible}
        onCancel={methods.closeCreateModal}
        onSubmit={methods.submitFollowRecord}
      />
    </V2Drawer>
  );
};

export default FollowRecordDrawer;
